import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { v4 as uuidv4 } from "uuid";
import Chat from "./Chat";
import "./styles/Widget.css";
import { setAuthToken, setWorkspace } from "../services/config";
import { addSystemMessage } from "../services";

const Widget = forwardRef(
  (
    {
      title = "Btime AI",
      headerColor = "#9728FD",
      position = "bottom-right",
      width = 350,
      height = 600,
      welcomeMessage = "Olá! Como posso ajudar você hoje?",
      autoOpenDelay = 0,
      buttonIcon = null,
      onClose,
      useNativeButton = true,
      customButtonComponent = null,
      token = null,
      workspace = null,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const widgetRef = useRef(null);
    const [messages, setMessages] = useState([]);

    // Inicializar o conversation ID quando o widget for montado
    useEffect(() => {
      if (!conversationId) {
        setConversationId(uuidv4());
      }
    }, [conversationId]);

    useImperativeHandle(ref, () => ({
      open: () => {
        setIsOpen(true);
        if (!conversationId) {
          setConversationId(uuidv4());
        }
      },
      close: () => {
        setIsOpen(false);
        if (onClose) onClose();
      },
      toggle: toggle,
      isOpen: () => isOpen,
      resetConversation: () => {
        // Gerar novo ID de conversa para forçar o reset das mensagens no componente Chat
        const newId = uuidv4();
        setConversationId(newId);
        return newId;
      },
      getConversationId: () => conversationId,
    }));

    useEffect(() => {
      setIsOpen(false);

      if (autoOpenDelay > 0) {
        const timer = setTimeout(() => {
          setIsOpen(true);
          setConversationId(uuidv4());
        }, autoOpenDelay);
        return () => clearTimeout(timer);
      }
    }, []);

    useEffect(() => {
      if (token) {
        setAuthToken(token);
      }

      if (workspace) {
        setWorkspace(workspace);
      }
    }, [token, workspace]);

    useEffect(() => {
      if (widgetRef.current) {
        widgetRef.current.toggle = toggle;
      }
    }, []);

    const toggle = () => {
      if (!isOpen) {
        if (!conversationId) {
          setConversationId(uuidv4());
        }
      }
      setIsOpen(!isOpen);
    };

    const adjustColor = (hex, amount) => {
      return (
        "#" +
        hex.replace(/^#/, "").replace(/../g, (color) => {
          const value = Math.min(
            255,
            Math.max(0, parseInt(color, 16) + amount)
          );
          return value.toString(16).padStart(2, "0");
        })
      );
    };

    const hexToRgba = (hex, alpha = 1) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const getPositionStyles = () => {
      const positions = {
        "bottom-right": { bottom: "20px", right: "20px" },
        "bottom-left": { bottom: "20px", left: "20px" },
        "top-right": { top: "20px", right: "20px" },
        "top-left": { top: "20px", left: "20px" },
      };

      return positions[position] || positions["bottom-right"];
    };

    const positionStyles = getPositionStyles();

    const widgetStyles = {
      container: {
        ...positionStyles,
        zIndex: 1000,
      },
      content: {
        width: `${width}px`,
        height: `${height}px`,
        ...positionStyles,
        bottom: positionStyles.bottom ? "90px" : undefined,
        top: positionStyles.top ? "90px" : undefined,
      },
      header: {
        backgroundColor: "white",
        color: "#333",
        borderBottom: `1px solid ${hexToRgba(headerColor, 0.08)}`,
      },
    };

    const buttonContent = buttonIcon || (
      <div className="chat-icon">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M19 6H5C3.89543 6 3 6.89543 3 8V16C3 17.1046 3.89543 18 5 18H8L12 22L16 18H19C20.1046 18 21 17.1046 21 16V8C21 6.89543 20.1046 6 19 6Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );

    return (
      <div
        ref={widgetRef}
        className={`chat-widget-container ${position} ${
          isOpen ? "open" : "closed"
        }`}
      >
        {isOpen && (
          <div className="chat-widget-content" style={widgetStyles.content}>
            <div className="chat-widget-app-container">
              <div className="chat-widget-header" style={widgetStyles.header}>
                <div className="header-content">
                  <div className="ai-logo">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 512 512"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g
                        transform="translate(0,512) scale(0.1,-0.1)"
                        fill="url(#sparkle-gradient)"
                      >
                        <path
                          d="M3230 4243 c-32 -16 -53 -54 -240 -424 -218 -432 -260 -499 -383
                      -615 -108 -104 -177 -144 -594 -353 -284 -142 -387 -198 -400 -218 -24 -36
                      -24 -110 0 -146 13 -20 113 -75 390 -213 205 -103 410 -211 457 -241 98 -64
                      227 -190 291 -283 24 -36 132 -240 239 -455 212 -423 220 -435 299 -435 79 0
                      85 8 290 417 206 411 255 495 354 600 107 113 192 165 615 378 432 218 442
                      225 442 305 0 80 -11 88 -436 300 -408 204 -472 242 -580 342 -123 115 -171
                      192 -383 617 -148 295 -207 402 -227 416 -40 26 -93 29 -134 8z"
                        />
                        <path
                          d="M818 1563 c-9 -10 -55 -96 -103 -192 -117 -236 -138 -256 -381 -379
                      -99 -50 -186 -98 -193 -106 -8 -9 -11 -26 -7 -41 5 -21 38 -41 188 -116 253
                      -127 276 -149 403 -400 96 -188 111 -208 153 -195 10 4 56 82 111 192 127 252
                      145 270 387 392 99 50 186 98 193 106 7 8 11 27 9 43 -3 25 -20 37 -192 124
                      -252 129 -266 143 -395 395 -91 179 -98 189 -127 192 -19 2 -36 -3 -46 -15z"
                        />
                      </g>
                      <defs>
                        <linearGradient
                          id="sparkle-gradient"
                          x1="0"
                          y1="0"
                          x2="512"
                          y2="512"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0" stopColor={headerColor} />
                          <stop
                            offset="0.5"
                            stopColor={adjustColor(headerColor, -20)}
                          />
                          <stop
                            offset="1"
                            stopColor={adjustColor(headerColor, -40)}
                          />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  <h1>{title}</h1>
                  <span className="beta-badge">Q&A Beta</span>

                  <div className="header-actions">
                    <button
                      className="close-button"
                      onClick={() => {
                        setIsOpen(false);
                        if (onClose) onClose();
                      }}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18M6 6L18 18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <div className="chat-widget-main">
                <div className="chat-widget-body">
                  <Chat
                    initialMessage={welcomeMessage}
                    token={token}
                    workspace={workspace}
                    conversationId={conversationId}
                  />
                </div>
              </div>
              <div className="chat-widget-footer">
                <p>&copy; {new Date().getFullYear()} - BTime AI Assistant</p>
              </div>
            </div>
          </div>
        )}

        {!isOpen && useNativeButton && (
          <button className="chat-widget-button" onClick={toggle}>
            {buttonContent}
          </button>
        )}

        {!isOpen && customButtonComponent && (
          <div className="custom-button-container" onClick={toggle}>
            {customButtonComponent}
          </div>
        )}
      </div>
    );
  }
);

export default Widget;
